<template>
  <b-container class="pt-2">
    <b-row v-if="loading">
      <font-awesome-icon icon="spinner" size="lg" spin />
    </b-row>
    <b-row v-if="!loading && comments && comments.length && showTypeSelection" align-h="center" class="pl-0 pr-0">
      <b-col cols="8">
        <b>Sort and display: </b><br>
        Toggle to group by and drag up/down to re-order
        <br>
        <input type="checkbox" v-model="combineDimensions"> Combine Categories
      </b-col>
      <b-col cols="4">
        <draggable v-model="displayOrder">
          <li v-for="displayOrderItem in displayOrder" style="border: 0px;" :key="`${combineDimensions}-${displayOrderItem.index}`" class="list-group-item">
            <toggle-button 
              v-model="displayOrderItem.enabled" 
              :labels="{checked: displayOrderItem.name, unchecked: displayOrderItem.name}" 
              :width="150" 
              :height="40" 
              :switch-color="{checked: '#218838', unchecked: '#aaaaaa'}" 
              :color="{checked: '#30c651', unchecked: '#cccccc'}"
            />
          </li>
        </draggable>
      </b-col>
    </b-row>
    <b-row v-if="!loading && comments && comments.length" align-h="center">
      <b-col cols=12>
        <CommentsSection :level="1" :comments="arrangedComments"/>
      </b-col>
    </b-row>
    <b-row v-if="!loading && (!comments || !comments.length)">
      No comments to display
    </b-row>
  </b-container>
</template>
<script>
import * as _ from 'lodash';
import * as crypto from 'crypto';
import * as Reporting from "@/services/Reporting";
import draggable from 'vuedraggable';
import vueConfirmationButton from 'vue-confirmation-button';
import CommentsSection from './CommentsSection';
import ToggleButton from 'vue-js-toggle-button/src/Button';

export default {
  name: 'CommentsList',
  components: {
    ToggleButton,
    draggable,
    CommentsSection
  },
  props: {
    dimensions: {
      default: null,
      type: Array
    },
    surveyRuns: {
      default: null,
      type: Array
    },
    showTypeSelection: {
      default: false,
      type: Boolean
    }
  },
  data: function () {
    return {
      comments: [],
      arrangedComments: [],
      loading: true,
      displayOrder: [],
      lastDisplayOrder: [],
      headerOrders: [],
      combineDimensions: true,
      myId: Math.round(Math.random() * 100000)
    };
  },
  watch: {
    dimensions: function (newVal) {
      this.load();
    },
    combineDimensions: function (newVal) {
      if (newVal === true) {
        this.displayOrder = this.combinedDisplayOrder;
      } else {
        this.displayOrder = this.uncombinedDisplayOrder;
      }
      /**
          The toggle has a bug.
        */
      this.displayOrder.forEach((item, index) => this.displayOrder[index].enabled = item.enabled);
    },
    displayOrder: {
      handler (newVal) {
        this.orderComments();
      },
      deep: true
    }
  },
  computed: {
    headings: function() {
      return [];
    },
    values: function() {
      return [];
    }
  },
  created: function () {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      return Reporting.getComments(this.surveyRuns.map(surveyRun => surveyRun.id), this.dimensions || [], this.$root.token).then(response => {
        // this is for display order later.
        this.combinedDisplayOrder = [
          {sortBy: 'element', enabled: true, name: 'Element', index: 0},
          {sortBy: 'dimensions', enabled: false, name: 'Categories', index: 1}
        ];
        this.uncombinedDisplayOrder = [
          {sortBy: 'element', enabled: true, name: 'Element', index: 0},
          ...(this.dimensions || []).map((dimension, index) => ({
            sortBy: `dimension${index}Value`,
            enabled: false,
            name: dimension.dimensionName,
            index: index + 1
          }))
        ];
        this.displayOrder = this.combinedDisplayOrder;
        this.comments = response.body;
        this.orderComments();
        this.combineDimensions = true;
        this.loading = false;
      });
    },
    orderComments() {
      /**
      Sort the comments using the displayOrder and combineDimensions variables
      Turn it into something that can be fed into CommentsSection.vue
      */
      // Case 1: No comments!
      if (!this.comments || this.comments.length === 0) {
        this.arrangedComments = new Map();
        return;
      }
      // Case 2: No breakdown!
      if (!this.displayOrder || this.displayOrder.length === 0 
        || !this.displayOrder.reduce((accumulator, orderItem) => (accumulator || orderItem.enabled), false)) {
        this.arrangedComments = new Map();
        this.arrangedComments.set('All', this.comments);
      }
      // Case 3: Recursive breakdown
      // 2On^2 kinda unavoidable
      // Let's do a very special sort!
      // Prep
      this.comments.forEach((comment) => comment.ordering = []);
      const applicableOrdering = this.displayOrder.filter((orderItem) => orderItem.enabled);
      const dimensionOrder = (this.dimensions || []).map((dimension) => dimension.responseValues.map((responseValue) => responseValue.as));

      // now the sort
      this.comments.sort((commentA, commentB) => {
        let i = 0;
        while (i < applicableOrdering.length) {
          const orderItem = applicableOrdering[i];
          if (orderItem.sortBy === 'element') {
            // element
            if (!commentA.ordering[i]) commentA.ordering.push(commentA.elementOrder);
            if (!commentB.ordering[i]) commentB.ordering.push(commentB.elementOrder);
            
            // different?
            if (commentA.ordering[i] !== commentB.ordering[i]) {
              return commentA.ordering[i] < commentB.ordering[i] ? -1 : 1;
            }
          } else if (orderItem.sortBy === 'dimensions') {
            // combined dimensions
            // go through the dimensions comparing.
            // we need to go down the dimensions, comparing as we go, until we find an inequality.
            if (!commentA.ordering[i]) {
              // we make a nasty string.
              commentA.ordering[i] = parseInt(dimensionOrder
                .map((ord, index) => {
                  let commentDimensionOrder = ord.indexOf(commentA[`dimension${index}Value`]);
                  // not specified?  end of the list!
                  if (commentDimensionOrder === -1) commentDimensionOrder = 9999;
                  return commentDimensionOrder;
                })
                .join('')), 10;
            }
            if (!commentB.ordering[i]) {
              // we make a nasty string.
              commentB.ordering[i] = parseInt(dimensionOrder
                .map((ord, index) => {
                  let commentDimensionOrder = ord.indexOf(commentB[`dimension${index}Value`]);
                  // not specified?  end of the list!
                  if (commentDimensionOrder === -1) commentDimensionOrder = 9999;
                  return `${commentDimensionOrder}`.padStart(4, '0');
                })
                .join('')), 10;
            }
            // different?
            if (commentA.ordering[i] !== commentB.ordering[i]) {
              return commentA.ordering[i] < commentB.ordering[i] ? -1 : 1;
            }
          } else {
            // specific dimension value
            const dimensionId = parseInt(orderItem.sortBy.replace(/[^0-9]/g, ''), 10);
            if (!commentA.ordering[i]) {
              let commentAOrder = dimensionOrder[dimensionId].indexOf(commentA[orderItem.sortBy]);
              if (commentAOrder === -1) commentAOrder = 9999;
              commentA.ordering.push(commentAOrder);
            }
            if (!commentB.ordering[i]) {
              let commentBOrder = dimensionOrder[dimensionId].indexOf(commentB[orderItem.sortBy]);
              if (commentBOrder === -1) commentBOrder = 9999;
              commentB.ordering.push(commentBOrder);
            }
            // different?
            if (commentA.ordering[i] !== commentB.ordering[i]) {
              return commentA.ordering[i] < commentB.ordering[i] ? -1 : 1;
            }
          }
          i+=1;
        }
      });

      // now the display!
      this.comments.forEach((comment) => {
        // this page cuts the data many ways so it's as simple
        // as I can get it.
        comment.orderingDisplay = [];
        applicableOrdering.forEach((orderItem) => {
          if (orderItem.sortBy === 'element') {
            // element
            comment.orderingDisplay.push(comment.elementName);
          } else if (orderItem.sortBy === 'dimensions') {
            const dimensionFields = Object.keys(comment).filter((key) => /^dimension/.test(key));
            const dimensionsValue = dimensionFields.map(dimensionField => comment[dimensionField]).join(', ');
            comment.orderingDisplay.push(dimensionsValue);
          } else {
            // specific dimension value
            comment.orderingDisplay.push(comment[orderItem.sortBy]);
          }
        });
      });
      
      // nest it suitable for display.
      // ew recursion.
      // no clue why but this function has to be defined each time.
      // use our recursive function
      this.arrangedComments = this.comments.reduce((accumulator, comment) => {
        const nestNext = (parent, index) => {
          if (index === comment.orderingDisplay.length - 1) {
            if (!parent.get(comment.orderingDisplay[index])) {
              parent.set(comment.orderingDisplay[index], []);
            }
            parent.get(comment.orderingDisplay[index]).push(comment);
          } else {
            if (!parent.get(comment.orderingDisplay[index])) parent.set(comment.orderingDisplay[index], new Map());
            nestNext(parent.get(comment.orderingDisplay[index]), index + 1);
          }
        };
        nestNext(accumulator, 0);
        return accumulator;
      }, new Map());
    }
  }
}
</script>
<style>
.btn{
    white-space:normal !important;
    word-wrap: break-word; 
}
</style>


// WEBPACK FOOTER //
// src/components/CommentsList.vue